<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

    <!-- Whole page only when the server allows it (platform company) -->
    <template v-if="!accessDenied">
    <!-- The Import Modal (4 steps) -->
    <div class="modal fade" tabindex="-1" data-bs-focus="false" data-bs-backdrop="static" data-bs-keyboard="false" id="migrationModal">
        <div class="modal-dialog modal-lg">
            <div class="modal-content bg-sa-modal-content">
                <!-- Modal body -->
                <div class="modal-body">
                    <div class="card bg-sa-card">
                        <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
                            <span><i class="fa fa-exchange me-2"></i>New Import</span>
                            <button type="button" class="btn-close color-sa-btn-close" data-bs-dismiss="modal" :disabled="isSaving"></button>
                        </div>
                        <div class="card-body bg-sa-card-body m-0 p-2">
                            <AlertComponent
                                v-if="defaultAlert.pageOrModal=='modal'"
                                :alertstatus="defaultAlert.alertstatus"
                                :message="defaultAlert.alertMessage"
                                :type="defaultAlert.alertType"
                            />

                            <!-- Wizard steps -->
                            <div class="wizard-steps mb-3">
                                <div
                                    v-for="(name, i) in stepNames"
                                    :key="i"
                                    class="wizard-step"
                                    :class="{
                                        'is-active': migrationData.step === i + 1,
                                        'is-done': migrationData.step > i + 1
                                    }"
                                >
                                    <div class="wizard-circle">
                                        <i v-if="migrationData.step > i + 1" class="fa fa-check"></i>
                                        <span v-else>{{ i + 1 }}</span>
                                    </div>
                                    <div class="wizard-label">{{ name }}</div>
                                </div>
                            </div>
                            <h5 class="sa-core-color mb-3">
                                <strong>Step {{ migrationData.step }}</strong> - {{ stepNames[migrationData.step - 1] }}
                            </h5>

                            <!-- ==================== STEP 1: UPLOAD ==================== -->
                                                        <!-- ==================== STEP 1: UPLOAD ==================== -->
                            <form v-if="migrationData.step === 1" id="migrationForm" novalidate @submit.prevent="uploadFile()">
                                <div class="row g-3 mb-0">
                                    <!-- What are you importing (full width) -->
                                    <SelectField
                                        v-model="formInputs.data_type"
                                        col="col-12"
                                        name="data_type"
                                        label="What are you importing?"
                                        icon="fa fa-th-list"
                                        :options="definitions"
                                        :reduce="(d) => d.data_type"
                                        :clearable="false"
                                        placeholder="Search what to import..."
                                        tooltip="Please choose what to import"
                                        :submitted="submitted"
                                        required
                                        @change="$clearValidationAlert('#migrationForm')"
                                    >
                                        <template #no-options>Nothing found.</template>
                                    </SelectField>

                                    <!-- Company -->
                                    <SelectField
                                        v-model="formInputs.target_company_id"
                                        col="col-md-6"
                                        name="target_company_id"
                                        label="Import into company"
                                        icon="fa fa-building"
                                        :options="companies"
                                        :get-option-label="(c) => c.company_name"
                                        :reduce="(c) => c.id"
                                        :clearable="false"
                                        placeholder="Search company..."
                                        tooltip="Please choose the company"
                                        :submitted="submitted"
                                        required
                                        @change="$clearValidationAlert('#migrationForm')"
                                    >
                                        <template #no-options>No company found.</template>
                                    </SelectField>

                                    <!-- Branch (only for modules with a branch) -->
                                    <SelectField
                                        v-if="showBranchSelect"
                                        v-model="formInputs.target_branch_id"
                                        col="col-md-6"
                                        label="Import into branch"
                                        icon="fa fa-code-fork"
                                        :options="branchOptions"
                                        :get-option-label="(b) => b.branch_name"
                                        :reduce="(b) => b.id"
                                        :clearable="false"
                                        :loading="branchesLoading"
                                        :disabled="!formInputs.target_company_id || branchesLoading"
                                        :placeholder="branchesLoading ? 'Loading branches...' : 'Search branch...'"
                                        hint="A branch column in the file takes priority."
                                    >
                                        <template #no-options>No branch found.</template>
                                    </SelectField>

                                    <!-- Old system name (suggestions from the list below) -->
                                    <FormField
                                        v-model="formInputs.source_system"
                                        col="col-md-6"
                                        label="Old system name"
                                        icon="fa fa-database"
                                        list="sourceSystems"
                                        placeholder="e.g. quickbooks"
                                        tooltip="Use the same name for every file from that system"
                                        required
                                        @input="handleInputChange"
                                        @change="handleInputChange"
                                    >
                                        <datalist id="sourceSystems">
                                            <option v-for="s in sourceSuggestions" :key="s" :value="s"></option>
                                        </datalist>
                                    </FormField>

                                    <!-- File (setting formImportData.dataFile to null empties the box) -->
                                    <FormField
                                        v-model="formImportData.dataFile"
                                        col="col-md-6"
                                        type="file"
                                        label="File (Excel or CSV, row 1 = column names)"
                                        icon="fa fa-file-excel-o"
                                        accept=".xlsx,.xls,.csv"
                                        tooltip="Please choose a file"
                                        required
                                        @change="onFileImportChange"
                                    />
                                </div>

                                <div v-if="selectedDefinition" class="mt-3 small">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <strong>Columns this import understands:</strong>
                                        <button type="button" class="btn btn-sm btn-success fw-semibold shadow-sm" :disabled="!!templateLoading" @click="downloadTemplate(formInputs.data_type)">
                                            <i class="me-1" :class="templateLoading === formInputs.data_type ? 'fa fa-cog fa-spin' : 'fa fa-file-excel-o'"></i>
                                            {{ templateLoading === formInputs.data_type ? 'Preparing...' : 'Download template' }}
                                        </button>
                                    </div>
                                    <span
                                        v-for="f in selectedDefinition.fields"
                                        :key="f.field"
                                        class="badge me-1 mb-1"
                                        :class="f.required ? 'bg-danger' : 'bg-secondary'"
                                    >
                                        {{ f.label }}<span v-if="f.required"> *</span>
                                    </span>
                                    <div class="text-muted mt-1 sa-core-color">* = required. Import in this order: Companies, then Branches, then Users.</div>
                                </div>
                            </form>

                            <!-- ==================== STEP 2: MATCH COLUMNS ==================== -->
                            <div v-if="migrationData.step === 2">
                                <p class="text-muted small mb-2 sa-core-color">
                                    For each column in your file, choose where it goes. "Don't import" columns are ignored.
                                    Your choices are remembered for the next file from <strong>{{ formInputs.source_system }}</strong>.
                                </p>
                                <div class="table-responsive">
                                    <table class="table table-sm align-middle mb-2">
                                        <thead>
                                            <tr>
                                                <th>Column in your file</th>
                                                <th class="hide-sm-td">Example value</th>
                                                <th style="min-width: 220px">Goes into</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="header in migrationData.headers" :key="header">
                                                <td><strong>{{ header }}</strong></td>
                                                <td class="hide-sm-td text-muted small sa-core-color">{{ sampleValue(header) }}</td>
                                                <td>
                                                    <v-select
                                                        v-model="migrationData.columnMap[header]"
                                                        :options="mapOptions"
                                                        label="label"
                                                        :reduce="(o) => o.field"
                                                        :clearable="false"
                                                        append-to-body
                                                        placeholder="Search field..."
                                                        :class="{ 'vs-invalid': isDuplicateChoice(header) }"
                                                    >
                                                        <template #no-options>No field found.</template>
                                                    </v-select>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-if="missingRequired.length" class="alert alert-warning py-2 small mb-0">
                                    Still needed: <strong>{{ missingRequired.join(', ') }}</strong>
                                </div>
                            </div>

                            <!-- ==================== STEP 3: CHECK RESULTS ==================== -->
                            <div v-if="migrationData.step === 3">
                                <p class="text-muted small mb-2 sa-core-color">Nothing has been saved yet.</p>
                                <div class="row g-2 mb-3 text-center">
                                    <div class="col-4">
                                        <div class="p-2 rounded bg-success bg-opacity-25">
                                            <div class="fs-4 fw-bold">{{ migrationData.counts.ready }}</div>
                                            <div class="small">Ready</div>
                                        </div>
                                    </div>
                                    <div class="col-4">
                                        <div class="p-2 rounded bg-danger bg-opacity-25">
                                            <div class="fs-4 fw-bold">{{ migrationData.counts.error }}</div>
                                            <div class="small">Errors</div>
                                        </div>
                                    </div>
                                    <div class="col-4">
                                        <div class="p-2 rounded bg-warning bg-opacity-25">
                                            <div class="fs-4 fw-bold">{{ migrationData.counts.duplicate }}</div>
                                            <div class="small">Duplicates (skipped)</div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="migrationData.problems.length">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <strong>Rows with problems</strong>
                                        <button type="button" class="btn btn-sm btn-outline-danger" :disabled="isSaving" @click="downloadProblems(migrationData.batchId)">
                                            <i class="fa fa-download me-1"></i>Download (fix in Excel, then upload again)
                                        </button>
                                    </div>
                                    <div class="table-responsive problems-box">
                                        <table class="table table-sm mb-0">
                                            <thead><tr><th>Row</th><th>Problem</th><th>Details</th></tr></thead>
                                            <tbody>
                                                <tr v-for="p in migrationData.problems" :key="p.row_number">
                                                    <td>{{ p.row_number }}</td>
                                                    <td>
                                                        <span class="badge" :class="p.status === 'duplicate' ? 'bg-warning text-dark' : 'bg-danger'">
                                                            {{ p.status === 'duplicate' ? 'Duplicate' : 'Error' }}
                                                        </span>
                                                    </td>
                                                    <td class="small">{{ p.error_message }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <!-- ==================== STEP 4: DONE ==================== -->
                            <div v-if="migrationData.step === 4" class="text-center py-3">
                                <i class="fa fa-check-circle fa-3x text-success mb-2"></i>
                                <h5 class="sa-core-color">{{ migrationData.doneMessage }}</h5>
                                <p class="text-muted small mb-0">
                                    You can undo this import from the list if something is wrong.
                                    <span v-if="formInputs.data_type === 'users'"><br />Imported users have no password and no role: assign roles, then users log in with "Forgot password".</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Modal footer -->
                <div class="modal-footer bg-sa-modal-footer">
                    <div class="d-flex justify-content-end flex-wrap gap-2">
                        <!-- Step 1 -->
                        <button v-if="migrationData.step === 1" type="submit" form="migrationForm" class="btn btn-primary" :disabled="isSaving">
                            <i :class="isSaving ? 'fa fa-cog fa-spin me-1' : 'fa fa-upload me-1'"></i>
                            {{ isSaving ? 'Uploading ' + formImportData.progress + '%' : 'Upload' }}
                        </button>

                        <!-- Step 2 -->
                        <template v-if="migrationData.step === 2">
                            <button type="button" class="btn btn-secondary" :disabled="isSaving" @click="clearForm()">
                                <i class="fa fa-arrow-left me-1"></i>Back
                            </button>
                            <button type="button" class="btn btn-primary" :disabled="isSaving || missingRequired.length > 0 || hasDuplicateChoice" @click="checkBatch()">
                                <i :class="isSaving ? 'fa fa-cog fa-spin me-1' : 'fa fa-search me-1'"></i>
                                {{ isSaving ? 'Checking...' : 'Check the file' }}
                            </button>
                        </template>

                        <!-- Step 3 -->
                        <template v-if="migrationData.step === 3">
                            <button type="button" class="btn btn-secondary" :disabled="isSaving" @click="migrationData.step = 2">Change matches</button>
                            <button type="button" class="btn btn-info" :disabled="isSaving" @click="clearForm()">Upload a fixed file</button>
                            <button type="button" class="btn btn-success" :disabled="isSaving || migrationData.counts.ready === 0" @click="importBatch(migrationData.batchId)">
                                <i :class="isSaving ? 'fa fa-cog fa-spin me-1' : 'fa fa-check me-1'"></i>
                                {{ isSaving ? 'Importing... please wait' : 'Import ' + migrationData.counts.ready + ' ready rows' }}
                            </button>
                        </template>

                        <!-- Step 4 -->
                        <button v-if="migrationData.step === 4" type="button" class="btn btn-primary" @click="clearForm(true)">
                            <i class="fa fa-plus me-1"></i>Start another import
                        </button>

                        <button type="button" class="btn btn-danger" data-bs-dismiss="modal" :disabled="isSaving">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- End The Import Modal -->

    <h4 class="sa-core-color fw-semibold mb-2">
        <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
    </h4>

<!-- Toggleable Tabs -->
<ul class="nav nav-tabs app-tabs mb-2" role="tablist">
    <li class="nav-item" role="presentation">
        <a href="#" class="nav-link" :class="{ 'app-tab-active': activeTab === 'checklist' }" @click.prevent="activeTab = 'checklist'">
            <span class="sa-core-color"><i class="fa fa-tasks me-1"></i>Checklist</span>
        </a>
    </li>
    <li class="nav-item" role="presentation">
        <a href="#" class="nav-link" :class="{ 'app-tab-active': activeTab === 'imports' }" @click.prevent="activeTab = 'imports'">
            <span class="sa-core-color"><i class="fa fa-history me-1"></i>Past imports</span>
        </a>
    </li>
</ul>
<!-- End Toggleable Tabs -->

<!-- Checklist tab -->
<div v-show="activeTab === 'checklist'">
<!-- Migration checklist -->
<div class="card glass mt-0 mb-3 bg-sa-card">
    <div class="card-body bg-sa-card-body p-0">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 p-2">
            <strong class="sa-core-color">
                <i class="fa fa-tasks me-1"></i>Migration checklist
                <span v-if="checklistCompanyId" class="text-muted fw-normal sa-core-color">- {{ companyName(checklistCompanyId) }}</span>
            </strong>
            <v-select
                v-model="checklistCompanyId"
                :options="companies"
                label="company_name"
                :reduce="(c) => c.id"
                :clearable="false"
                placeholder="Search company..."
                class="checklist-company-select"
            >
                <template #no-options>No company found.</template>
            </v-select>
        </div>
        <div v-if="checklistCompanyId" class="table-responsive">
            <table class="table table-sm align-middle mb-0">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Module</th>
                        <th class="hide-sm-td">Needs first</th>
                        <th class="hide-sm-td">Records</th>
                        <th>Status</th>
                        <th class="text-end">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="group in checklistGroups" :key="group.area">
                    <tr role="button" tabindex="0" class="checklist-area" :data-area="group.area"
                        :class="[openArea === group.area ? 'checklist-area-open' : 'table-light', { 'checklist-area-selected': selectedArea === group.area }]"
                        @focus="selectedArea = group.area"
                        @keydown.enter.prevent="toggleArea(group.area)"
                        @keydown.space.prevent="toggleArea(group.area)"
                        @keydown.up.prevent="moveArea(-1)"
                        @keydown.down.prevent="moveArea(1)">
                        <td colspan="6" class="fw-bold sa-core-color">
                            <i class="fa fa-lg me-2 checklist-toggle" :class="openArea === group.area ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"
                               @click="toggleArea(group.area)"></i>
                            {{ group.area }}
                            <span class="badge ms-2" :class="group.done === group.items.length ? 'bg-success' : 'bg-secondary'">
                                {{ group.done }} / {{ group.items.length }} done
                            </span>
                        </td>
                    </tr>
                    <tr v-for="(item, index) in group.items" :key="item.data_type" v-show="openArea === group.area" class="checklist-item">
                        <td>{{ index + 1 }}</td>
                        <td>{{ item.label }}</td>
                        <td class="hide-sm-td small">{{ item.requires.length ? item.requires.join(', ') : '-' }}</td>
                        <td class="hide-sm-td small">
                            {{ item.records }}
                            <span v-if="item.imported" class="text-muted sa-core-color">({{ item.imported }} imported)</span>
                        </td>
                        <td>
                            <span class="badge" :class="checklistStatusClass(item.status)">{{ checklistStatusLabel(item) }}</span>
                            <div v-if="item.status === 'locked'" class="small text-muted">Needs: {{ item.missing.join(', ') }}</div>                            
                        </td>
                        <td class="text-end">
                            <button type="button" class="btn btn-sm btn-success me-1 shadow-sm" title="Download template" :disabled="!!templateLoading" @click="downloadTemplate(item.data_type)">
                                <i :class="templateLoading === item.data_type ? 'fa fa-cog fa-spin' : 'fa fa-download'"></i>
                            </button>
                            <!-- Unfinished upload: go to it (import or cancel) -->
                            <button v-if="item.status === 'in_progress'" type="button" class="btn btn-sm btn-outline-warning"
                                    :disabled="isSaving"
                                    @click="showPendingImports(item)">
                                <i class="fa fa-play me-1"></i>Continue importing
                            </button>
                            <!-- Otherwise: start a new import -->
                            <button v-else type="button" class="btn btn-sm"
                                    :class="item.status === 'locked' ? 'btn-outline-secondary' : 'btn-outline-primary'"
                                    :disabled="item.status === 'locked' || isSaving"
                                    data-bs-toggle="modal" data-bs-target="#migrationModal"
                                    @click="startFromChecklist(item)">
                                <i class="fa fa-upload me-1"></i>Import
                            </button>
                        </td>
                    </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <p v-else class="text-muted small px-2 sa-core-color">Choose a company to see what has been migrated and what is left.</p>
    </div>
</div>


</div>
<!-- End Checklist tab -->

<!-- Past imports tab -->
<div v-show="activeTab === 'imports'">
<!--Tables-->
<div class="card glass mt-0 bg-sa-card">
    <div class="card-body bg-sa-card-body p-0">
        <div class="d-flex justify-content-between align-items-center p-2">
            <strong class="sa-core-color">
                Past imports
                <span class="text-muted fw-normal sa-core-color">- {{ tableData.filterCompany ? companyName(tableData.filterCompany) : 'All companies' }}</span>
            </strong>
            <div class="d-flex gap-1">
                <button
                    @click="clearForm(true)"
                    id="addRecordBtn"
                    title="New import (Alt+N)"
                    class="btn btn-success btn-sm"
                    data-bs-toggle="modal"
                    data-bs-target="#migrationModal"
                    :disabled="isSaving"
                >
                    <i class="fa fa-plus"></i><span class="d-none d-sm-inline"> New Import</span>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" title="Refresh" @click="fetchRecords()">
                    <i class="fa fa-refresh"></i>
                </button>
            </div>
        </div>
        <!-- Filters -->
        <div class="d-flex flex-wrap gap-2 p-2 pt-0 bg-table-search-panel">
            <v-select
                v-model="tableData.filterStatus"
                :options="statusFilterOptions"
                label="label"
                :reduce="(o) => o.value"
                :clearable="false"
                placeholder="Search status..."
                class="filter-select"
            >
                <template #no-options>No status found.</template>
            </v-select>
            <v-select
                v-model="tableData.filterCompany"
                :options="companyFilterOptions"
                label="company_name"
                :reduce="(c) => c.id"
                :clearable="false"
                placeholder="Search company..."
                class="filter-select"
            >
                <template #no-options>No company found.</template>
            </v-select>
            <v-select
                v-model="tableData.filterType"
                :options="typeFilterOptions"
                label="label"
                :reduce="(d) => d.data_type"
                :clearable="false"
                placeholder="Search type..."
                class="filter-select"
            >
                <template #no-options>No type found.</template>
            </v-select>
            <select v-model="tableData.recordsPerPage" @change="onFilterChange" class="form-select form-select-sm entries-select ms-auto">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
            </select>
        </div>
        <div class="table-responsive">
            <table
                class="table table-hover align-middle mb-0"
                id="bootstrapTable"
                tabindex="0"
                @keydown.up="$moveRowSelection(-1, $event)"
                @keydown.down="$moveRowSelection(1, $event)"
                @keydown.enter="$onTableEnter($event)"
            >
                <thead>
                    <tr>
                        <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
                        <th class="hide-sm-td">No</th>
                        <th class="hide-sm-td">Import No.</th>
                        <th>Type</th>
                        <th class="hide-sm-td">Into company</th>
                        <th class="hide-sm-td">Source / File</th>
                        <th class="hide-sm-td">Status</th>
                        <th class="hide-sm-td">Rows</th>
                        <th class="hide-sm-td">Date</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- No rows: blank while loading, then the empty message in the middle -->
                    <tr v-if="tableData.records.length === 0" class="table-filler">
                        <td colspan="10" class="text-center align-middle h-240">
                            <TableEmptyState
                                v-if="!tableLoading"
                                :has-filters="importFiltersOn"
                                :can-add="true"
                                :title="importFiltersOn ? '' : 'No imports yet'"
                                :message="importFiltersOn ? '' : 'Click New Import to bring data in from your old system.'"
                                icon="fa fa-exchange"
                                add-modal="#migrationModal"
                                add-label="New Import"
                                @add="clearForm(true)"
                                @clear="clearImportFilters"
                            />
                        </td>
                    </tr>
                    <template v-for="(record, index) in tableData.records" :key="record.id">
                    <tr class="clickable-row"
                        :class="{ 'active-row main-row': tableData.selectedRecordId === record.id }"
                        @click="$onRowClick(record, $event)">
                        <td class="expand-cell hide-sm-td"
                            :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                            @click="$toggleDetails(record.id)">
                        <i class="fa fa-lg expand-icon ms-1"
                          :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                          :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                        </td>
                        <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                        <td class="hide-sm-td">{{ record.migration_batch_number }}</td>
                        <td>
                            <!-- Phones: ⊕ and the number go in front of the type (their columns are hidden) -->
                            <span
                                class="show-sm-inline expand-cell me-2"
                                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                @click="$toggleDetails(record.id)"
                            >
                                <i class="fa fa-lg expand-icon"
                                   :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                                   :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                            </span>
                            <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.data_type_label }}
                        </td>
                        <td class="hide-sm-td">
                            {{ companyName(record.target_company_id) }}
                            <span v-if="record.target_branch_name" class="d-block small text-muted">{{ record.target_branch_name }}</span>
                        </td>
                        <td class="hide-sm-td small sa-core-color">{{ record.source_system }}<br /><span class="text-muted sa-core-color">{{ record.file_name }}</span></td>
                        <td class="hide-sm-td"><span class="badge" :class="statusClass(record.status)">{{ statusLabel(record.status) }}</span></td>
                        <td class="hide-sm-td small">
                            {{ record.total_rows }} total<br />
                            <span class="text-success">{{ record.imported_rows }} imported</span>
                            <span v-if="Number(record.error_rows)" class="text-danger"> · {{ record.error_rows }} errors</span>
                        </td>
                        <td class="hide-sm-td small">{{ $ConvertToLocalDate(record.date_created) }}</td>
                        <td class="text-end">
                            <div class="btn-group btn-group-sm">
                                <button v-if="record.status === 'uploaded'" type="button" class="btn btn-primary"
                                        :disabled="isSaving" title="Continue to Match columns" @click="continueBatch(record)">
                                    <i class="fa fa-play me-1"></i>Continue
                                </button>
                                <button v-if="['checked', 'importing'].includes(record.status)" type="button" class="btn btn-success"
                                        :disabled="isSaving" @click="importBatch(record.id)">
                                    {{ record.status === 'importing' ? 'Continue import' : 'Import' }}
                                </button>
                                <button v-if="Number(record.error_rows) || Number(record.duplicate_rows)" type="button" class="btn btn-outline-danger"
                                        :disabled="isSaving" title="Download problem rows" @click="downloadProblems(record.id)">
                                    <i class="fa fa-download"></i>
                                </button>
                                <button v-if="['imported', 'importing'].includes(record.status)" type="button" class="btn btn-outline-warning"
                                        :disabled="isSaving"
                                        :data-bs-toggle="'modal'"
                                        :data-bs-target="'#undoMigrationModal' + record.id">
                                    <i class="fa fa-undo me-1"></i>Undo
                                </button>
                                <button v-if="['uploaded', 'checked'].includes(record.status)" type="button" class="btn btn-outline-danger"
                                        :disabled="isSaving"
                                        :data-bs-toggle="'modal'"
                                        :data-bs-target="'#cancelMigrationModal' + record.id">
                                    <i class="fa fa-times me-1"></i>Cancel
                                </button>
                            </div>

                            <!--Undo Modal-->
                            <Teleport to="body">
                                <div :id="'undoMigrationModal' + record.id" class="modal fade" tabindex="-1">
                                    <div class="modal-dialog">
                                        <div class="modal-content bg-sa-modal-content">
                                            <div class="modal-header bg-sa-modal-header">
                                                <h4 class="modal-title sa-core-color w-100 text-center">Undo {{ record.migration_batch_number }}?</h4>
                                                <button type="button" class="btn-close color-sa-btn-close" data-bs-dismiss="modal"></button>
                                            </div>
                                            <div class="modal-body">
                                                <p class="sa-core-color w-100 text-center">
                                                    Everything this import created ({{ record.imported_rows }} records) will be removed.
                                                </p>
                                            </div>
                                            <div class="modal-footer bg-sa-modal-footer">
                                                <button @click="undoBatch(record)" type="button" class="btn btn-default btn-danger me-auto" data-bs-dismiss="modal">Yes</button>
                                                <button type="button" class="btn btn-default btn-success modal_close color-sa-modal_close" data-bs-dismiss="modal">No</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Teleport>
                            <!--End Undo Modal-->

                            <!--Cancel Modal-->
                            <Teleport to="body">
                                <div :id="'cancelMigrationModal' + record.id" class="modal fade" tabindex="-1">
                                    <div class="modal-dialog">
                                        <div class="modal-content bg-sa-modal-content">
                                            <div class="modal-header bg-sa-modal-header">
                                                <h4 class="modal-title sa-core-color w-100 text-center">Cancel {{ record.migration_batch_number }}?</h4>
                                                <button type="button" class="btn-close color-sa-btn-close" data-bs-dismiss="modal"></button>
                                            </div>
                                            <div class="modal-body">
                                                <p class="sa-core-color w-100 text-center">
                                                    This upload was never imported, so no records were saved. It will be removed and you can start again.
                                                </p>
                                            </div>
                                            <div class="modal-footer bg-sa-modal-footer">
                                                <button @click="cancelBatch(record)" type="button" class="btn btn-default btn-danger me-auto" data-bs-dismiss="modal">Yes</button>
                                                <button type="button" class="btn btn-default btn-success modal_close color-sa-modal_close" data-bs-dismiss="modal">No</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Teleport>
                            <!--End Cancel Modal-->
                        </td>
                    </tr>
                    <!-- Hidden expanded row -->
                    <tr class="collapse-row">
                        <td class="p-0 hide-sm-td"></td>
                        <td class="p-0 hide-sm-td"></td>
                        <td colspan="8" class="p-0">
                            <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                                <div class="p-3 details-list">
                                    <!-- Shown only on phones (hidden columns) -->
                                    <div class="show-sm-td"><i class="fa fa-hashtag"></i><strong>Import No.:</strong> {{ record.migration_batch_number }}</div>
                                    <div class="show-sm-td">
                                        <i class="fa fa-check-circle"></i><strong>Status:</strong>
                                        <span class="badge" :class="statusClass(record.status)">{{ statusLabel(record.status) }}</span>
                                    </div>
                                    <div class="show-sm-td"><i class="fa fa-building"></i><strong>Into Company:</strong> {{ companyName(record.target_company_id) }}</div>
                                    <div v-if="record.target_branch_name" class="show-sm-td"><i class="fa fa-code-fork"></i><strong>Into Branch:</strong> {{ record.target_branch_name }}</div>
                                    <div class="show-sm-td"><i class="fa fa-database"></i><strong>Source:</strong> {{ record.source_system }}</div>
                                    <div class="show-sm-td"><i class="fa fa-file"></i><strong>File:</strong> {{ record.file_name }}</div>
                                    <div class="show-sm-td"><i class="fa fa-list"></i><strong>Total Rows:</strong> {{ record.total_rows }}</div>
                                    <div class="show-sm-td"><i class="fa fa-check"></i><strong>Imported:</strong> {{ record.imported_rows }}</div>
                                    <div class="show-sm-td"><i class="fa fa-times"></i><strong>Errors:</strong> {{ record.error_rows }}</div>
                                    <div class="show-sm-td"><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>

                                    <!-- Always in details -->
                                    <div><i class="fa fa-clone"></i><strong>Duplicates:</strong> {{ record.duplicate_rows }}</div>
                                    <div v-if="record.finished_at"><i class="fa fa-calendar-check-o"></i><strong>Finished:</strong> {{ $ConvertToLocalDate(record.finished_at) }}</div>
                                    <div v-if="record.undone_at"><i class="fa fa-undo"></i><strong>Undone:</strong> {{ $ConvertToLocalDate(record.undone_at) }}</div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </template>
                </tbody>
                <tfoot class="table-light">
                    <tr>
                        <td colspan="10">
                            <!--Pagination (hidden when there are no rows; its space is kept)-->
                            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                            <Pagination
                                v-else
                                :currentPage="tableData.currentPage"
                                :totalPages="tableData.totalPages"
                                :hasNextPage="tableData.hasNextPage"
                                :totalRecords="tableData.totalRecords"
                                :startRecord="tableData.startRecord"
                                :endRecord="tableData.endRecord"
                                :showFullPagination="tableData.totalRecords > 0"
                                :lastKnownPage="lastKnownPage"
                                @page-changed="fetchRecords"
                                @loaderMessage="updateLoaderMessage"
                                @last-page-request="goToLastPage"
                            />
                            <!--End Pagination-->
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</div>
<!--End Tables-->
</div>
<!-- End Past imports tab -->
    </template>

    <!-- Not allowed: title and message only -->
    <template v-else>
        <h4 class="sa-core-color fw-semibold mb-2">
            <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
        </h4>
        <div class="alert alert-warning">
            <i class="fa fa-lock me-1"></i>
            Data migration is not available for this company. Check your role or upgrade your package.
        </div>
    </template>

</template>
<script>
    import AlertComponent from "../../../components/ui/AlertComponent.vue";
    import PageStatus from "../../../components/admin/PageStatus.vue";
    import Pagination from "../../../components/admin/Pagination.vue";
    import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
    import FormField from "../../../components/admin/FormField.vue";
    import SelectField from "../../../components/admin/SelectField.vue";
    import vSelect from "vue-select";
    import "vue-select/dist/vue-select.css";
    import dataMigrationsMixin from "./mixins/dataMigrationsMixin.js";
    import axios from "axios";

    const API = "/settings/api_migrations.php";

    export default {
        name: "DataMigration",
        mixins: [dataMigrationsMixin], // Inject all methods here
        components: {
            AlertComponent,
            PageStatus,
            Pagination,
            TableEmptyState,
            vSelect,     // still used in Match columns, the checklist and the filters
            FormField,
            SelectField,
        },
        data() {
            return {
                submitted: false,
                isSaving: false,
                expandedRows: {},
                stepNames: ["Upload", "Match columns", "Check", "Done"],
                sourceSuggestions: ["quickbooks", "sage", "odoo", "tally", "zoho", "excel"],
                definitions: [],
                companies: [],
                branches: [],            // branches of the company chosen in the import window
                branchesLoading: false,  // spinner in the branch box
                activeTab: 'checklist', // 'checklist' or 'imports'
                accessDenied: false,    // true = the server refused (not the platform company)
                templateLoading: "",    // module whose template is being prepared
                checklist: [],
                checklistCompanyId: "",
                openArea: "",            // the one open checklist group ("" = all closed)
                selectedArea: "",        // the highlighted checklist group (click / arrows)
                formInputs: this.getDefaultFormInputs(),
                formImportData: this.getDefaultFormImportData(), //For the file upload
                migrationData: this.getDefaultMigrationData(), //For the steps
                tableData: this.getDefaultTableData(),
                lastKnownPage: null,
                tableLoading: false,     // true while Past imports is loading (the empty message waits)
                defaultAlert: this.getDefaultAlert(),
                pageIcon: 'fa fa-exchange', // same icon as this page in the sidebar
            };
        },
        watch: {
            // New company chosen in the checklist: load its checklist
            checklistCompanyId() {
                this.fetchChecklist();
            },
            // Import window: new company = its branches, branch back to Head Office
            "formInputs.target_company_id"(companyId) {
                this.formInputs.target_branch_id = "";
                this.fetchBranches(companyId);
            },
            // Past imports filters: loader + back to page 1
            "tableData.filterStatus"() {
                this.onFilterChange();
            },
            "tableData.filterCompany"() {
                this.onFilterChange();
            },
            "tableData.filterType"() {
                this.onFilterChange();
            },
        },
        mounted() {
            this.$enableTableShortcuts();
        },
        beforeUnmount() {
            this.$disableTableShortcuts();
        },
        async created() {
            // Platform only: if the server refuses, stop here (no tables, no modal)
            await this.fetchDefinitions();
            if (this.accessDenied) {
                return;
            }
            this.fetchRecords();
            await this.fetchCompanies();
        },
        computed: {
            pageTitle() {
                return "Data Migration";
            },
            // Past imports filters (each starts with an "All" choice)
            statusFilterOptions() {
                return [
                    { value: "hide_undone", label: "All except undone" },
                    { value: "", label: "All imports" },
                    { value: "uploaded", label: "Uploaded (not checked yet)" },
                    { value: "checked", label: "Checked (not imported yet)" },
                    { value: "importing", label: "Importing" },
                    { value: "imported", label: "Imported" },
                    { value: "undone", label: "Undone" },
                ];
            },
            companyFilterOptions() {
                return [{ id: "", company_name: "All companies" }, ...this.companies];
            },
            typeFilterOptions() {
                return [{ data_type: "", label: "All types" }, ...this.definitions];
            },
            selectedDefinition() {
                return this.definitions.find((d) => d.data_type === this.formInputs.data_type) || null;
            },
            // Step 2 "Goes into": Don't import + every field (* = required)
            mapOptions() {
                const fields = this.selectedDefinition ? this.selectedDefinition.fields : [];
                return [
                    { field: "", label: "- Don't import -" },
                    ...fields.map((f) => ({ field: f.field, label: f.label + (f.required ? " *" : "") })),
                ];
            },
            // Only modules with a branch (CRUD, Users...) ask for one
            showBranchSelect() {
                return !!(this.selectedDefinition && this.selectedDefinition.uses_branch);
            },
            // Branches except the Head Office (it is the default choice)
            otherBranches() {
                return this.branches.filter((b) => !Number(b.is_head_office));
            },
            branchOptions() {
                return [{ id: "", branch_name: "Head Office (default)" }, ...this.otherBranches];
            },
            // Required fields not chosen for any column yet
            missingRequired() {
                if (!this.selectedDefinition) return [];
                const chosen = Object.values(this.migrationData.columnMap);
                return this.selectedDefinition.fields.filter((f) => f.required && !chosen.includes(f.field)).map((f) => f.label);
            },
            hasDuplicateChoice() {
                return this.migrationData.headers.some((h) => this.isDuplicateChoice(h));
            },
            // Past imports: a filter other than the defaults is on (for the empty message)
            importFiltersOn() {
                const t = this.tableData;
                return t.filterStatus !== "hide_undone" || !!t.filterCompany || !!t.filterType;
            },
        },
        methods: {
            getDefaultFormInputs() {
                return {
                    //Form Data (step 1)
                    data_type: "",
                    target_company_id: "",
                    target_branch_id: "",   // "" = Head Office / from the file
                    source_system: "",
                };
            },
            getDefaultFormImportData() {
                return {
                    dataFile: null,
                    uploading: false,
                    progress: 0,
                    message: "",
                    messageType: "",
                };
            },
            getDefaultMigrationData() {
                return {
                    step: 1,
                    batchId: null,
                    headers: [],
                    sample_rows: [],
                    columnMap: {},
                    counts: { ready: 0, error: 0, duplicate: 0 },
                    problems: [],
                    doneMessage: "",
                };
            },
            getDefaultTableData() {
                return {
                    //Table Data
                    records: [],
                    currentPage: 1,
                    totalPages: 1,
                    recordsPerPage: this.$recordsPerPage,
                    totalRecords: 0,
                    startRecord: 0,
                    endRecord: 0,
                    hasNextPage: false,
                    isVisible: false,
                    selectedRecordId: "",
                    filterStatus: "hide_undone", // undone imports hidden by default
                    filterCompany: "",
                    filterType: "",
                    loaderMessage: this.$loaderMessages.loaderMessageDefault,
                    loaderValue: null,
                    simulatedProgressInterval: null,
                };
            },
            getDefaultAlert() {
                return {
                    //For Alerts
                    alertMessage: "",
                    alertstatus: "",
                    showAlert: "",
                    alertType: "",
                    button_text: "",
                    pageOrModal: "",
                };
            },

            /* ---------- Lists ---------- */
            async fetchDefinitions() {
                await axios
                    .get(`${API}?action=fetchDefinitions`, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.definitions = response.data.results;
                        } else if (!this.handleAuthError(response)) {
                            this.accessDenied = true;
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "page"));
            },
            async fetchCompanies() {
                await axios
                    .get(`${API}?action=fetchCompanies`, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.companies = response.data.results;
                            if (!this.checklistCompanyId && this.companies.length) {
                                this.checklistCompanyId = this.companies[0].id;
                            }
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "page"));
            },
            fetchRecords(page = this.tableData.currentPage, loadingMessage = this.$loaderMessages.loaderMessageDefault) {
                this.$showOrHideLoader(loadingMessage);
                this.tableLoading = true;

                const params = new URLSearchParams({
                    action: "fetchBatches",
                    page: page,
                    limit: this.tableData.recordsPerPage,
                    status: this.tableData.filterStatus,
                    company: this.tableData.filterCompany,
                    type: this.tableData.filterType,
                });

                return axios
                    .get(`${API}?${params}`, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleFetchSuccess(response);
                            this.lastKnownPage = response.data.results.hasNextPage ? null : response.data.results.current_page;
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "page"))
                    .finally(() => {
                        this.tableData.isVisible = false;
                        this.tableLoading = false;
                    });
            },
            async goToLastPage() {
                this.tableData.isVisible = true;
                try {
                    const params = new URLSearchParams({
                        action: "getLastPage",
                        limit: this.tableData.recordsPerPage,
                        status: this.tableData.filterStatus,
                        company: this.tableData.filterCompany,
                        type: this.tableData.filterType,
                    });
                    const response = await axios.get(`${API}?${params}`, this.$handleConfig());

                    if (response.data.status === "success") {
                        const lastPage = response.data.results.lastPage;
                        await this.fetchRecords(lastPage);
                        this.lastKnownPage = lastPage;
                        this.tableData.totalPages = lastPage;
                    } else if (!this.handleAuthError(response)) {
                        this.handleError("ending", response.data.message || "Unable to find the last page.", "danger", "page");
                    }
                } catch (error) {
                    this.handleAlert("ending", "Unable to find the last page. Please try again.", "danger", "page");
                }
            },
            updateLoaderMessage(message) {
                this.tableData.loaderMessage = message;
            },
            fetchBranches(companyId) {
                this.branches = [];
                if (!companyId) {
                    return;
                }
                this.branchesLoading = true;

                axios
                    .get(`${API}?action=fetchBranches&company_id=${companyId}`, this.$handleConfig())
                    .then((response) => {
                        // Ignore an old answer if the company was changed again meanwhile
                        if (Number(companyId) !== Number(this.formInputs.target_company_id)) {
                            return;
                        }
                        if (response.data.status == "success") {
                            this.branches = response.data.results;
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "modal");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "modal"))
                    .finally(() => {
                        if (Number(companyId) === Number(this.formInputs.target_company_id)) {
                            this.branchesLoading = false;
                        }
                    });
            },
            /* ---------- Migration checklist ---------- */
            fetchChecklist() {
                if (!this.checklistCompanyId) {
                    this.checklist = [];
                    return;
                }
                // Show the loader (set directly: the toggle could hide one already showing)
                this.tableData.loaderMessage = this.$loaderMessages.loaderMessageDefault;
                this.tableData.isVisible = true;

                axios
                    .get(`${API}?action=fetchChecklist&company_id=${this.checklistCompanyId}`, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.checklist = response.data.results;
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "page"))
                    .finally(() => {
                        this.tableData.isVisible = false;
                    });
            },

            /* ---------- Step 1: upload ---------- */
            uploadFile() {
                if (this.isSaving) return;

                this.submitted = true; // red border on empty searchable boxes
                const formIsValid = this.$validateForm("#migrationForm");
                const choicesMade = this.formInputs.data_type && this.formInputs.target_company_id;
                if (!formIsValid || !choicesMade || !this.formImportData.dataFile) {
                    this.$playErrorSound();
                    this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                    return;
                }

                this.isSaving = true;
                this.handleUploadProgress();
                this.handleAlert("starting", this.$loaderMessages.uploading, "info", "modal");

                const formData = new FormData();
                formData.append("action", "uploadFile");
                formData.append("data_type", this.formInputs.data_type);
                formData.append("target_company_id", this.formInputs.target_company_id);
                formData.append("target_branch_id", this.showBranchSelect ? this.formInputs.target_branch_id : "");
                formData.append("source_system", this.formInputs.source_system);
                formData.append("file", this.formImportData.dataFile);

                axios
                    .post(API, formData, {
                        ...this.$handleConfig(),
                        onUploadProgress: (progressEvent) => {
                            if (progressEvent.total) {
                                this.formImportData.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            }
                        },
                    })
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleUploadSuccess(response);
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "modal");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "modal"))
                    .finally(() => {
                        this.stopUploadProgress();
                        this.isSaving = false;
                    });
            },

            /* ---------- Step 2: check (dry run) ---------- */
            checkBatch() {
                if (this.isSaving) return;
                this.isSaving = true;
                this.handleAlert("starting", "Checking every row. Please wait...", "info", "modal");

                const formData = new FormData();
                formData.append("action", "checkBatch");
                formData.append("batch_id", this.migrationData.batchId);
                formData.append("column_map", JSON.stringify(this.migrationData.columnMap));

                axios
                    .post(API, formData, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleCheckSuccess(response);
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "modal");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "modal"))
                    .finally(() => {
                        this.isSaving = false;
                    });
            },

            /* ---------- Step 3: import ---------- */
            importBatch(batchId) {
                if (this.isSaving) return;
                this.isSaving = true;

                // Messages inside the window if it is open, otherwise on the page
                const where = this.isModalOpen() ? "modal" : "page";
                if (where === "page") {
                    this.$showOrHideLoader("Importing. Please keep this page open...");
                }
                this.handleAlert("starting", "Importing... big files can take a few minutes. Please keep this page open.", "info", where);

                const formData = new FormData();
                formData.append("action", "importBatch");
                formData.append("batch_id", batchId);

                axios
                    .post(API, formData, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleImportSuccess(response, where);
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", where);
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, where))
                    .finally(() => {
                        this.isSaving = false;
                        this.tableData.isVisible = false;
                        this.fetchRecords();
                    });
            },

            /* ---------- Continue an upload at "Match columns" ---------- */
            async continueBatch(record) {
                if (this.isSaving) return;
                this.tableData.loaderMessage = this.$loaderMessages.loaderMessageDefault;
                this.tableData.isVisible = true;

                try {
                    const response = await axios.get(`${API}?action=fetchBatchMapping&batch_id=${record.id}`, this.$handleConfig());

                    if (response.data.status !== "success") {
                        if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                        return;
                    }

                    const r = response.data.results;

                    // Fill step 1 (as it was uploaded), then go straight to step 2
                    this.clearForm(true);
                    this.formInputs.data_type = r.data_type;
                    this.formInputs.target_company_id = r.target_company_id;
                    this.formInputs.source_system = r.source_system;
                    await this.$nextTick(); // the company watcher resets the branch first
                    this.formInputs.target_branch_id = r.target_branch_id;

                    this.migrationData.batchId = r.batch_id;
                    this.migrationData.headers = r.headers;
                    this.migrationData.sample_rows = r.sample_rows;
                    this.migrationData.columnMap = { ...r.suggested_map };
                    this.migrationData.step = 2;

                    this.$showModal("migrationModal");
                } catch (error) {
                    this.handleNetworkError(error, "page");
                } finally {
                    this.tableData.isVisible = false;
                }
            },
            /* ---------- Cancel (upload never imported) ---------- */
            cancelBatch(record) {
                if (this.isSaving) return;
                this.isSaving = true;
                this.tableData.loaderMessage = "Cancelling...";
                this.tableData.isVisible = true;

                const formData = new FormData();
                formData.append("action", "cancelBatch");
                formData.append("batch_id", record.id);

                axios
                    .post(API, formData, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleAlert("ending", response.data.message, "success", "page");
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "page"))
                    .finally(() => {
                        this.isSaving = false;
                        this.tableData.isVisible = false;
                        this.fetchRecords();
                    });
            },
            // Empty message "Clear filters": Past imports back to the default filters
            clearImportFilters() {
                this.tableData.filterStatus = "hide_undone";
                this.tableData.filterCompany = "";
                this.tableData.filterType = "";
            },
            // Checklist "Continue or cancel it": Past imports, showing that module's unfinished uploads
            showPendingImports(item) {
                this.tableData.filterStatus = "";
                this.tableData.filterCompany = this.checklistCompanyId;
                this.tableData.filterType = item.data_type;
                this.activeTab = "imports";
            },
            /* ---------- Undo ---------- */
            undoBatch(record) {
                if (this.isSaving) return;
                this.isSaving = true;
                this.$showOrHideLoader("Undoing import...");

                const formData = new FormData();
                formData.append("action", "undoBatch");
                formData.append("batch_id", record.id);

                axios
                    .post(API, formData, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleAlert("ending", response.data.message, "success", "page");
                        } else if (!this.handleAuthError(response)) {
                            this.handleError("ending", response.data.message, "danger", "page");
                        }
                    })
                    .catch((error) => this.handleNetworkError(error, "page"))
                    .finally(() => {
                        this.isSaving = false;
                        this.tableData.isVisible = false;
                        this.fetchRecords();
                    });
            },

            /* ---------- Import template (columns + sample rows) ---------- */
            async downloadTemplate(dataType) {
                if (!dataType || this.templateLoading) return;
                const where = this.isModalOpen() ? "modal" : "page";

                // Page: the big loader. Modal: the button's spinner (the page loader is behind the modal)
                this.templateLoading = dataType;
                if (where === "page") {
                    this.tableData.loaderMessage = "Preparing the template...";
                    this.tableData.isVisible = true;
                }

                try {
                    const response = await axios.get(`${API}?action=downloadTemplate&data_type=${dataType}`, {
                        ...this.$handleConfig(),
                        responseType: "blob",
                    });

                    // An error comes back as JSON instead of a file
                    if (response.data.type && response.data.type.includes("json")) {
                        const data = JSON.parse(await response.data.text());
                        if (!this.handleAuthError({ data })) {
                            this.handleAlert("ending", data.message, "danger", where);
                        }
                        return;
                    }

                    const url = window.URL.createObjectURL(response.data);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", `${dataType}_import_template.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } catch (error) {
                    this.handleNetworkError(error, where);
                } finally {
                    this.templateLoading = "";
                    if (where === "page") {
                        this.tableData.isVisible = false;
                    }
                }
            },
            /* ---------- Download problem rows (original columns + Problem) ---------- */
            async downloadProblems(batchId) {
                if (this.isSaving) return;
                this.isSaving = true;
                const where = this.isModalOpen() ? "modal" : "page";

                try {
                    const rows = [];
                    for (const status of ["error", "duplicate"]) {
                        let page = 1;
                        while (true) {
                            const response = await axios.get(
                                `${API}?action=fetchBatchRows&batch_id=${batchId}&status=${status}&page=${page}&limit=200`,
                                this.$handleConfig()
                            );
                            if (response.data.status != "success") {
                                if (!this.handleAuthError(response)) {
                                    this.handleError("ending", response.data.message, "danger", where);
                                }
                                return;
                            }
                            rows.push(...response.data.results.rows);
                            if (page * response.data.results.limit >= response.data.results.total) break;
                            page++;
                        }
                    }

                    if (rows.length === 0) {
                        this.handleAlert("ending", "There are no problem rows.", "info", where);
                        return;
                    }
                    this.downloadProblemsCsv(rows, batchId);
                } catch (error) {
                    this.handleNetworkError(error, where);
                } finally {
                    this.isSaving = false;
                }
            },
        },
    };
</script>